import Layouts from '@/components/ToolPopovers/Layout';
import PpButton from '@/components/Buttons/Button';
import CommonModal from '@/components/Modals/CommonModal';
import MappingPreview from './MappingPreview';
import ConfirmAction from '@/containers/Modals/ConfirmAction';

import {
  useModal,
  useGetterTheme,
  useCustomLayout,
  useActionLayout,
  useGetLayouts,
  useGetDigitalLayouts,
  useLayoutElements,
  useMappingTemplate
} from '@/hooks';
import { getThemeByIdApi, getThemesApi } from '@/api/theme';
import {
  ASSORTED_TYPE_VALUE,
  CUSTOM_LAYOUT_TYPE,
  DIGITAL_LAYOUT_TYPES,
  LAYOUT_PAGE_TYPE,
  PER_PAGE_10,
  PRINT_LAYOUT_TYPES,
  SAVED_AND_FAVORITES_TYPE
} from '@/common/constants';
import { isEmpty, isHalfSheet } from '@/common/utils';
import { get } from 'lodash';

export default {
  components: { Layouts, CommonModal, PpButton, MappingPreview, ConfirmAction },
  setup() {
    const { toggleModal } = useModal();
    const { getDefaultThemeId } = useGetterTheme();
    const { getCustom, getCustomDigitalLayout } = useCustomLayout();
    const { getFavoriteLayouts, getFavorites } = useActionLayout();
    const {
      getPrintLayouts: fetchPrintLayouts,
      getDigitalLayouts: fetchDigitalLayouts,
      getPrintLayoutByType,
      getDigitalLayoutByType,
      getAssortedLayoutCategories,
      getAssortedLayouts
    } = useGetLayouts();

    const { getDigitalLayoutElements } = useGetDigitalLayouts();
    const { getLayoutElements } = useLayoutElements();
    const { deleteTemplateMapping } = useMappingTemplate();

    return {
      toggleModal,
      getDefaultThemeId,
      getCustom,
      getCustomDigitalLayout,
      getFavoriteLayouts,
      fetchPrintLayouts,
      fetchDigitalLayouts,
      getDigitalLayoutElements,
      getPrintLayoutByType,
      getDigitalLayoutByType,
      getAssortedLayoutCategories,
      getLayoutElements,
      deleteTemplateMapping,
      getAssortedLayouts,
      getFavorites
    };
  },
  data() {
    const printText = {
      title: 'Step 1: Select a Print Layout',
      optionTitle: 'Layout Type:'
    };
    const digitalText = {
      title: 'Step 2: Select a Digital Layout',
      optionTitle: 'Screen Type:'
    };
    return {
      printText,
      digitalText,
      printLayoutSelected: null,
      digitalLayoutSelected: null,
      printLayouts: [],
      digitalLayouts: [],
      extraPrintLayouts: [],
      extraDigitalLayouts: [],
      customPrintLayouts: [],
      customDigitalLayouts: [],
      favoritePrintLayoutIds: [],
      favoriteDigitalLayouts: [],
      themesOptions: [],
      digitalThemesOptions: [],
      printLayoutTypes: [],
      printThemeSelected: {},
      printDefaultTheme: null,
      printLayoutTypeSelected: {},
      digitalLayoutTypes: [],
      digitalThemeSelected: {},
      digitalDefaultTheme: null,
      digitalLayoutTypeSelected: {},
      isDigitalOpaque: true,
      isPrintFooterHidden: false,
      isDigitalFooterHidden: false,
      isPrintPreviewDisplayed: false,
      isDigitalPreviewDisplayed: false,
      isStepThreeDisplayed: false,
      isConfirmDisplayed: false,
      assortedLayouts: [],
      waitingLayout: null,
      isDeleteMapModalDisplayed: false,
      isReassignModalDisplayed: false,
      currentPage: 1,
      isLoadingCategory: true,
      perPages: PER_PAGE_10,
      currentPageExtraLayouts: 1,
      isLoadingExtraPrintLayouts: true,
      isLoadingExtraDigitalLayouts: true,
      isLoadingPrintLayouts: false,
      currentPageAssortedLayoutPrint: 1,
      tabType: null,
      isLoadingDigitalLayouts: false,
      currentPageDigitalLayout: 1,
      isLoadingDigitalTheme: true,
      currentPageDigitalTheme: 1,
      currentPageFavPrintLayout: 1,
      isLoadingPrintTheme: true,
      currentPagePrintTheme: 1,
      currentPageSpreadLayout: 1,
      currentPageSingleLayout: 1,
      currentPageUserSpreadLayouts: 0,
      currentPageUserSingleLayouts: 0,
      currentPageUserDigitalLayouts: 1
    };
  },
  computed: {
    isDisablePrintTheme() {
      return this.isSaveAndFavoriteType(this.printLayoutTypeSelected);
    },
    isDisableDigitalTheme() {
      return this.isSaveAndFavoriteType(this.digitalLayoutTypeSelected);
    },
    printLayoutId() {
      return this.printLayoutSelected?.id || null;
    },
    digitalLayoutId() {
      return this.digitalLayoutSelected?.id || null;
    },
    activeTab() {
      this.tabType = isHalfSheet ? 0 : 1;
      return isHalfSheet ? 1 : 0;
    }
  },
  async mounted() {
    const fetchData = [this.initData(), this.getAssorted()];
    await Promise.all(fetchData);

    this.printLayoutTypes = this.handleLayoutTypes();
    this.digitalLayoutTypes = this.handleLayoutTypes();

    this.updateLayoutTypes();

    this.printLayoutTypeSelected = this.printLayoutTypes[0];
    this.digitalLayoutTypeSelected = this.digitalLayoutTypes[0];

    const printTheme = this.themesOptions.find(
      t => t.id === this.printDefaultTheme
    );
    if (!printTheme && this.printDefaultTheme) {
      const printThemeOpt = await getThemeByIdApi(this.printDefaultTheme);
      if (!isEmpty(printThemeOpt)) this.themesOptions.push(printThemeOpt);
    }
    this.printThemeSelected = this.themesOptions.find(
      t => t.id === this.printDefaultTheme
    );
    const digitalTheme = this.digitalThemesOptions.find(
      t => t.id === this.digitalDefaultTheme
    );
    if (!digitalTheme && this.digitalDefaultTheme) {
      const digitalThemeOpt = await getThemeByIdApi(
        this.digitalDefaultTheme,
        true
      );
      if (!isEmpty(digitalThemeOpt))
        this.digitalThemesOptions.push(digitalThemeOpt);
    }
    this.digitalThemeSelected = this.digitalThemesOptions.find(
      t => t.id === this.digitalDefaultTheme
    );

    await Promise.all([this.getPrintLayouts(), this.getDigitalLayouts()]);

    this.handleStepOne();
  },
  methods: {
    isSaveAndFavoriteType(type) {
      return (
        type.value === CUSTOM_LAYOUT_TYPE || type.value === ASSORTED_TYPE_VALUE
      );
    },
    onConfirmPrintLayout(layout) {
      if (isEmpty(layout) || layout.mappings) return;

      this.printLayoutSelected = layout;

      this.handleStepTwo();
    },
    async onConfirmDigitalLayout(layout) {
      if (isEmpty(layout) || layout.mappings) return;

      const layoutEle = await this.getDigitalLayoutElements(layout.id);

      this.digitalLayoutSelected = layoutEle;
      this.handleStepThree();
    },
    async onChangePrintTheme(theme) {
      this.printThemeSelected = theme;
      this.printLayoutTypeSelected = this.printLayoutTypes[0];
      this.currentPageSingleLayout = 1;
      this.currentPageSpreadLayout = 1;
      this.getPrintLayouts();
    },
    async onChangeDigitalTheme(theme) {
      this.digitalThemeSelected = theme;
      this.digitalLayoutTypeSelected = this.digitalLayoutTypes[0];
      this.currentPageDigitalLayout = 1;
      this.getDigitalLayouts();
    },
    onChangePrintLayoutType(type) {
      this.printLayoutTypeSelected = isEmpty(type)
        ? { sub: '' }
        : { value: type.value, sub: type.sub?.value };
      this.currentPageExtraLayouts = 1;
      this.currentPageAssortedLayoutPrint = 1;
      this.currentPageFavPrintLayout = 1;
      this.currentPageSingleLayout = 1;
      this.currentPageSpreadLayout = 1;
      this.currentPageUserSingleLayouts = 0;
      this.currentPageUserSpreadLayouts = 0;
      this.getPrintLayouts();
    },
    onChangeDigitalLayoutType(type) {
      this.digitalLayoutTypeSelected = type;
      this.currentPageExtraLayouts = 1;
      this.currentPageDigitalLayout = 1;
      this.currentPageUserDigitalLayouts = 1;
      this.getDigitalLayouts();
    },
    /**
     *  Trigger when user hit confirm button
     *  switch to mapping element modal
     */
    onConfirm(print, digital, config) {
      const printLayout = print || this.printLayoutSelected;
      const digitalLayout = digital || this.digitalLayoutSelected;

      this.$emit('onConfirm', {
        printLayout,
        digitalLayout,
        config
      });
    },
    onCancel() {
      this.$emit('onCancel');
    },
    updateLayoutTypes() {
      this.printLayoutTypes.splice(
        this.printLayoutTypes.length - 1,
        0,
        SAVED_AND_FAVORITES_TYPE
      );

      this.digitalLayoutTypes.splice(
        this.digitalLayoutTypes.length - 1,
        0,
        SAVED_AND_FAVORITES_TYPE
      );
    },
    async initData() {
      this.themesOptions = await getThemesApi(false, 1);
      this.digitalThemesOptions = await getThemesApi(true, 1);

      const theme = await this.getDefaultThemeId();
      this.digitalDefaultTheme = theme.digitalDefaultTheme;
      this.printDefaultTheme = theme.printDefaultTheme;

      this.favoritePrintLayoutIds = await this.getFavorites();
    },
    /**
     * Get print layouts
     */
    async getPrintLayouts() {
      if (isEmpty(this.printLayoutTypeSelected)) return;
      const { value: typeValue, sub: subValue } = this.printLayoutTypeSelected;

      const isAssorted = typeValue === PRINT_LAYOUT_TYPES.ASSORTED.value;

      if (isAssorted) {
        this.printLayouts = await this.getAssortedLayouts(
          subValue,
          this.currentPageAssortedLayoutPrint,
          false
        );
        this.isLoadingPrintLayouts = true;
        const tabLayouts = this.printLayouts.filter(
          l => l.pageType === this.tabType
        );
        if (
          tabLayouts.length === 0 &&
          this.printLayouts.length === this.perPages
        ) {
          this.onLoadMorePrintLayouts(true);
        }
        return;
      }

      if (typeValue === SAVED_AND_FAVORITES_TYPE.value) {
        this.printLayouts = await this.fetchPrintLayouts(
          this.printThemeSelected?.id,
          typeValue
        );
        this.isLoadingPrintLayouts = true;
        if (this.printLayouts.length < this.perPages)
          this.currentPageFavPrintLayout = -1;
        const tabLayouts = this.printLayouts.filter(
          l => l.pageType === this.tabType
        );
        if (tabLayouts.length === 0) {
          this.loadMoreSavAndFavPrintLayouts();
        }
        return;
      }
      this.printLayouts =
        this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id
          ? await this.fetchPrintLayouts(
              this.printThemeSelected?.id,
              typeValue,
              this.tabType,
              this.currentPageSingleLayout
            )
          : await this.fetchPrintLayouts(
              this.printThemeSelected?.id,
              typeValue,
              this.tabType,
              this.currentPageSpreadLayout
            );
      this.isLoadingPrintLayouts = true;

      // if layout type is ALL => do not load  extra layouts of other themes
      if (typeValue === PRINT_LAYOUT_TYPES.ALL.value) return;

      const { extraLayouts, isContinue } = await this.getPrintLayoutByType(
        this.printThemeSelected?.id,
        this.printLayoutTypeSelected?.value,
        1,
        false
      );
      this.isLoadingExtraPrintLayouts = true;
      this.extraPrintLayouts = extraLayouts;
      const layoutType =
        this.tabType === LAYOUT_PAGE_TYPE.FULL_PAGE.id
          ? LAYOUT_PAGE_TYPE.FULL_PAGE.type
          : LAYOUT_PAGE_TYPE.SINGLE_PAGE.type;
      const tabLayouts = extraLayouts.filter(l => l.type === layoutType);
      if (tabLayouts.length === 0 && isContinue) {
        this.onLoadMoreExtraPrintLayouts(true);
      }
    },
    /**
     * Get digital layouts
     */
    async getDigitalLayouts() {
      const { value: typeValue } = this.digitalLayoutTypeSelected;

      const isAssorted = typeValue === DIGITAL_LAYOUT_TYPES.ASSORTED.value;

      if (isAssorted) {
        this.digitalLayouts = [];
        return;
      }

      if (typeValue === SAVED_AND_FAVORITES_TYPE.value) {
        const customLayouts = await this.getCustomDigitalLayout(
          this.currentPageUserDigitalLayouts
        );
        this.isLoadingDigitalLayouts = true;
        this.digitalLayouts = customLayouts.filter(
          layout => !layout.isSupplemental
        );
        if (
          customLayouts.length === this.perPages &&
          this.digitalLayouts.length === 0
        )
          await this.onLoadMoreUserDigitalLayouts(false);
        return;
      }

      this.digitalLayouts = await this.fetchDigitalLayouts(
        this.digitalThemeSelected?.id,
        this.digitalLayoutTypeSelected?.value,
        false,
        this.currentPageDigitalLayout,
        false
      );
      this.isLoadingDigitalLayouts = true;

      // if layout type is ALL => do not load  extra layouts of other themes
      if (typeValue === DIGITAL_LAYOUT_TYPES.ALL.value) return;

      // load more layout of the other themes
      const { extraLayouts, isContinue } = await this.getDigitalLayoutByType(
        this.digitalThemeSelected?.id,
        this.digitalLayoutTypeSelected?.value,
        1,
        false
      );
      this.isLoadingExtraDigitalLayouts = true;
      this.extraDigitalLayouts = extraLayouts;
      if (extraLayouts.length === 0 && isContinue) {
        this.onLoadMoreExtraDigitalLayouts(true);
      }
    },
    /**
     * Get assoreted layout from server
     */
    async getAssorted() {
      const assorted = await this.getAssortedLayoutCategories(this.currentPage);
      this.assortedLayouts = assorted;
    },
    /**
     * Step: Select print layout
     */
    handleStepOne() {
      this.isDigitalOpaque = true;
      this.isDigitalFooterHidden = true;
      this.isPrintFooterHidden = false;
      this.isPrintPreviewDisplayed = false;
      this.isDigitalPreviewDisplayed = false;
      this.isStepThreeDisplayed = false;

      if (this.isConfirmDisplayed) {
        this.handleEditPrint();
      }

      this.isConfirmDisplayed = false;
    },
    /**
     * Step: Select digital layout
     */
    handleStepTwo() {
      this.isDigitalOpaque = false;
      this.isPrintFooterHidden = true;
      this.isPrintPreviewDisplayed = true;

      if (this.isDigitalPreviewDisplayed) {
        this.handleStepThree();
        return;
      }
      this.isDigitalFooterHidden = false;
      this.isStepThreeDisplayed = false;
      this.isConfirmDisplayed = false;
    },
    /**
     * Show confirm button, and preview thumbnails of print and digital layouts
     */
    handleStepThree() {
      this.isPrintFooterHidden = true;
      this.isConfirmDisplayed = true;
      this.isDigitalFooterHidden = true;
      this.isDigitalPreviewDisplayed = true;
      this.isStepThreeDisplayed = true;
    },
    handleEditPrint() {
      this.isDigitalPreviewDisplayed = true;
    },
    /**
     * Handle logic when user click on edit selection on Print layout
     */
    editPrintSelection() {
      this.currentPageExtraLayouts = 1;
      this.currentPageAssortedLayoutPrint = 1;
      this.currentPageFavPrintLayout = 1;
      this.currentPageSingleLayout = 1;
      this.currentPageSpreadLayout = 1;
      this.currentPageUserSingleLayouts = 0;
      this.currentPageUserSpreadLayouts = 0;
      this.tabType = 1;
      this.handleStepOne();
      this.getPrintLayouts();
    },
    /**
     * Handle logic when user click on edit selection on Digital layout
     */
    editDigitalSelection() {
      this.currentPageExtraLayouts = 1;
      this.currentPageDigitalLayout = 1;
      this.currentPageUserDigitalLayouts = 1;
      this.isDigitalPreviewDisplayed = false;
      this.handleStepTwo();
      this.getDigitalLayouts();
    },
    handleLayoutTypes() {
      const types = Object.values(PRINT_LAYOUT_TYPES).map(lt => ({
        ...lt,
        subItems: []
      }));

      const assortedType = types.filter(
        l => l.value === PRINT_LAYOUT_TYPES.ASSORTED.value
      )[0];

      assortedType.subItems = this.assortedLayouts.map(({ id, name }) => ({
        id,
        name,
        value: id,
        shortName: `Assorted: ${name}`
      }));

      return types;
    },
    /**
     * Selecte print and digital layouts and
     * switch to mapping element modal
     *
     * @param {Object} layout layout data
     */
    async onEditMap(layout) {
      const printLayoutId = layout.id;
      const digitalLayoutId = get(layout, 'mappings.theOtherLayoutId');
      const config = layout.mappings;

      const printObjects = await this.getLayoutElements(printLayoutId);
      const printLayout = { ...layout, objects: printObjects };
      const digitalLayout = await this.getDigitalLayoutElements(
        digitalLayoutId
      );

      this.onConfirm(printLayout, digitalLayout, config);
    },
    async onReassignMap() {
      this.printLayoutSelected = this.waitingLayout;

      // delete mappings
      await this.onDeleteMap();

      this.onCloseReassignModal();
      this.handleStepTwo();
    },
    /**
     * Call api to delete mappings
     *
     * @param {Object} layout layout data
     */
    async onDeleteMap() {
      this.onCloseDeleteConfirmModal();

      await this.deleteTemplateMapping(this.waitingLayout.mappings);

      // remove overlay on UI of digital template
      const digitalLayout = [
        ...this.digitalLayouts,
        ...this.extraDigitalLayouts
      ].find(lo => lo.id === this.waitingLayout.mappings.theOtherLayoutId);

      if (digitalLayout?.mappings) digitalLayout.mappings = null;

      // remove overlay on UI of print template
      this.waitingLayout.mappings = null;
      this.waitingLayout = null;
    },
    /**
     * Show confirm model when use hit delete mapping button
     */
    showDeleteMapModal(layout) {
      this.waitingLayout = layout;

      this.isDeleteMapModalDisplayed = true;
    },
    /**
     * Hide delete confirm model
     */
    onCloseDeleteConfirmModal() {
      this.isDeleteMapModalDisplayed = false;
    },
    /**
     * Show reassign confirm model when use hit reassign mapping button
     */
    showReassignConfirmModal(layout) {
      this.waitingLayout = layout;

      this.isReassignModalDisplayed = true;
    },
    /**
     * Hide reassign confirm model
     */
    onCloseReassignModal() {
      this.isReassignModalDisplayed = false;
    },
    async onLoadMoreCategory() {
      if (this.currentPage > 0) {
        this.currentPage++;
        const addAssorted = await this.getAssortedLayoutCategories(
          this.currentPage,
          true
        );
        if (addAssorted.length < this.perPages) {
          this.isLoadingCategory = false;
          this.currentPage = -1;
        }
        this.assortedLayouts = [...this.assortedLayouts, ...addAssorted];
        this.assortedLayouts.sort((item1, item2) =>
          item1.name.localeCompare(item2.name)
        );
        this.printLayoutTypes = this.handleLayoutTypes();
        this.digitalLayoutTypes = this.handleLayoutTypes();
        this.updateLayoutTypes();
      }
    },
    async onLoadMoreExtraPrintLayouts(needFreeze) {
      const hideSpiner = needFreeze ? false : true;
      const layoutType =
        this.tabType === LAYOUT_PAGE_TYPE.FULL_PAGE.id
          ? LAYOUT_PAGE_TYPE.FULL_PAGE.type
          : LAYOUT_PAGE_TYPE.SINGLE_PAGE.type;
      if (this.currentPageExtraLayouts > 0) {
        this.currentPageExtraLayouts++;
        const { extraLayouts, isContinue } = await this.getPrintLayoutByType(
          this.printThemeSelected?.id,
          this.printLayoutTypeSelected?.value,
          this.currentPageExtraLayouts,
          hideSpiner
        );
        if (!isContinue) {
          this.currentPageExtraLayouts = -1;
          this.isLoadingExtraPrintLayouts = false;
        }
        this.extraPrintLayouts = [...this.extraPrintLayouts, ...extraLayouts];
        const tabLayouts = extraLayouts.filter(l => l.type === layoutType);
        //Because There are two tab spread and single so data return in 1 page can < 4.
        //if data < 4 can't get scroll event to call next page API.
        if (tabLayouts.length < 4) {
          this.extraPrintLayouts.length === 0
            ? this.onLoadMoreExtraPrintLayouts(true)
            : this.onLoadMoreExtraPrintLayouts(false);
        }
      }
    },
    async onLoadMoreExtraDigitalLayouts(needFreeze) {
      const hideSpiner = needFreeze ? false : true;
      if (this.currentPageExtraLayouts > 0) {
        this.currentPageExtraLayouts++;
        const { extraLayouts, isContinue } = await this.getDigitalLayoutByType(
          this.digitalThemeSelected?.id,
          this.digitalLayoutTypeSelected?.value,
          this.currentPageExtraLayouts,
          hideSpiner
        );
        if (!isContinue) {
          this.currentPageExtraLayouts = -1;
          this.isLoadingExtraDigitalLayouts = false;
        }
        this.extraDigitalLayouts = [
          ...this.extraDigitalLayouts,
          ...extraLayouts
        ];
        if (extraLayouts.length < 4) {
          this.extraDigitalLayouts.length === 0
            ? this.onLoadMoreExtraDigitalLayouts(true)
            : this.onLoadMoreExtraDigitalLayouts(false);
        }
      }
    },
    async onChangeTab(tab) {
      this.tabType = tab === 0 ? 1 : 0;
      this.isLoadingPrintLayouts = true;
      const layoutTab = this.printLayouts.filter(
        l => l.pageType === this.tabType
      );
      if (isEmpty(layoutTab)) {
        if (this.printLayoutTypeSelected.value < 0) {
          await this.onLoadMorePrintLayouts(true);
        } else {
          const layouts =
            this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id
              ? await this.fetchPrintLayouts(
                  this.printThemeSelected?.id,
                  this.printLayoutTypeSelected.value,
                  this.tabType,
                  this.currentPageSingleLayout
                )
              : await this.fetchPrintLayouts(
                  this.printThemeSelected?.id,
                  this.printLayoutTypeSelected.value,
                  this.tabType,
                  this.currentPageSpreadLayout
                );
          this.printLayouts = [...this.printLayouts, ...layouts];
        }
      }
    },
    async onLoadMorePrintLayouts(needFreeze) {
      const hideSpiner = needFreeze ? false : true;
      const subValue = this.printLayoutTypeSelected.sub;
      const isAssorted =
        this.printLayoutTypeSelected.value ===
        PRINT_LAYOUT_TYPES.ASSORTED.value;
      const isSavAndFav =
        this.printLayoutTypeSelected.value === SAVED_AND_FAVORITES_TYPE.value;
      if (isAssorted) {
        this.loadMoreAssortedPrintLayouts(subValue, hideSpiner);
        return;
      }
      if (isSavAndFav) {
        this.loadMoreSavAndFavPrintLayouts(hideSpiner);
        return;
      }
      await this.loadMorePrintLayoutsByTheme(hideSpiner);
    },

    async loadMorePrintLayoutsByTheme(hideSpiner) {
      const typeValue = this.printLayoutTypeSelected.value;
      if (this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id) {
        if (this.currentPageSingleLayout < 0) {
          this.isLoadingPrintLayouts = false;
          return;
        }
        this.currentPageSingleLayout++;
        const layouts = await this.fetchPrintLayouts(
          this.printThemeSelected?.id,
          typeValue,
          this.tabType,
          this.currentPageSingleLayout,
          hideSpiner
        );
        this.printLayouts = [...this.printLayouts, ...layouts];
        if (layouts.length < this.perPages) {
          this.isLoadingPrintLayouts = false;
          this.currentPageSingleLayout = -1;
        }
      } else {
        if (this.currentPageSpreadLayout < 0) {
          this.isLoadingPrintLayouts = false;
          return;
        }
        this.currentPageSpreadLayout++;
        const layouts = await this.fetchPrintLayouts(
          this.printThemeSelected?.id,
          typeValue,
          this.tabType,
          this.currentPageSpreadLayout,
          hideSpiner
        );
        this.printLayouts = [...this.printLayouts, ...layouts];
        if (layouts.length < this.perPages) {
          this.isLoadingPrintLayouts = false;
          this.currentPageSpreadLayout = -1;
        }
      }
    },
    async loadMoreAssortedPrintLayouts(subValue, hideSpiner) {
      if (this.currentPageAssortedLayoutPrint < 0) {
        this.isLoadingPrintLayouts = false;
        return;
      }
      this.currentPageAssortedLayoutPrint++;
      const assortedLayouts = await this.getAssortedLayouts(
        subValue,
        this.currentPageAssortedLayoutPrint,
        hideSpiner
      );
      if (assortedLayouts.length < this.perPages) {
        this.isLoadingPrintLayouts = false;
        this.currentPageAssortedLayoutPrint = -1;
      }
      this.printLayouts = [...this.printLayouts, ...assortedLayouts];
      const tabLayouts = assortedLayouts.filter(
        l => l.pageType === this.tabType
      );
      const layouts = this.printLayouts.filter(
        l => l.pageType === this.tabType
      );
      //Because There are two tab spread and single/layout_type = null so data return in 1 page can < 4.
      //if data < 4 can't get scroll event to call next page API.
      if (tabLayouts.length < 4) {
        layouts.length === 0
          ? this.onLoadMorePrintLayouts(true)
          : this.onLoadMorePrintLayouts(false);
      }
    },
    async onLoadMoreDigitalLayouts() {
      if (
        this.digitalLayoutTypeSelected.value === SAVED_AND_FAVORITES_TYPE.value
      ) {
        this.onLoadMoreUserDigitalLayouts(true);
        return;
      }
      if (this.currentPageDigitalLayout < 0) {
        this.isLoadingDigitalLayouts = false;
        return;
      }
      this.currentPageDigitalLayout++;
      const layouts = await this.fetchDigitalLayouts(
        this.digitalThemeSelected?.id,
        this.digitalLayoutTypeSelected?.value,
        false,
        this.currentPageDigitalLayout,
        true
      );
      if (layouts.length < this.perPages) {
        this.isLoadingDigitalLayouts = false;
        this.currentPageDigitalLayout = -1;
      }
      this.digitalLayouts = [...this.digitalLayouts, ...layouts];
    },
    async onLoadMoreUserDigitalLayouts(hideSpiner) {
      if (this.currentPageUserDigitalLayouts < 0) {
        this.isLoadingDigitalLayouts = false;
        return;
      }
      this.currentPageUserDigitalLayouts++;
      const customLayouts = await this.getCustomDigitalLayout(
        this.currentPageUserDigitalLayouts,
        hideSpiner
      );
      if (customLayouts.length < this.perPages) {
        this.isLoadingDigitalLayouts = false;
        this.currentPageUserDigitalLayouts = -1;
      }
      const userLayouts = customLayouts.filter(
        layout => !layout.isSupplemental
      );
      userLayouts.forEach(item => {
        const index = this.digitalLayouts.findIndex(
          layout => layout.id === item.id
        );
        if (index < 0) this.digitalLayouts.push(item);
      });
      this.digitalLayouts.sort((item1, item2) => item1.id - item2.id);
      //Because There are two type layout supplemental and no supplemental so data return in 1 page can < 4.
      //if data < 4 can't get scroll event to call next page API.
      if (userLayouts.length < 4) {
        this.digitalLayouts.length === 0
          ? await this.onLoadMoreUserDigitalLayouts(false)
          : await this.onLoadMoreUserDigitalLayouts(true);
      }
    },
    async onLoadMoreDigitalTheme() {
      if (this.currentPageDigitalTheme < 0) {
        this.isLoadingDigitalTheme = false;
        return;
      }
      this.currentPageDigitalTheme++;
      const themes = await getThemesApi(
        true,
        this.currentPageDigitalTheme,
        true
      );
      this.digitalThemesOptions = [...this.digitalThemesOptions, ...themes];
      this.digitalThemesOptions.sort((item1, item2) => item1.id - item2.id);
      if (themes.length < this.perPages) {
        this.isLoadingDigitalTheme = false;
        this.currentPageDigitalTheme = -1;
      }
    },
    async loadMoreSavAndFavPrintLayouts(hideSpiner) {
      if (
        this.currentPageFavPrintLayout < 0 &&
        this.currentPageUserSingleLayouts < 0 &&
        this.currentPageUserSpreadLayouts < 0
      ) {
        this.isLoadingPrintLayouts = false;
        return;
      }
      if (this.currentPageFavPrintLayout < 0) {
        await this.loadMoreUserPrintLayouts(hideSpiner);
        return;
      }
      this.currentPageFavPrintLayout++;
      const extraFavLayouts = await this.getFavoriteLayouts(
        this.currentPageFavPrintLayout,
        hideSpiner
      );
      this.printLayouts = [...this.printLayouts, ...extraFavLayouts];
      if (extraFavLayouts.length < this.perPages) {
        this.currentPageFavPrintLayout = -1;
      }
      const tabLayouts = extraFavLayouts.filter(
        l => l.pageType === this.tabType
      );
      const layouts = this.printLayouts.filter(
        l => l.pageType === this.tabType
      );
      //Because There are two tab spread and single so data return in 1 page can < 4.
      //if data < 4 can't get scroll event to call next page API.
      if (tabLayouts.length < 4) {
        layouts.length === 0
          ? this.loadMoreSavAndFavPrintLayouts(false)
          : this.loadMoreSavAndFavPrintLayouts(true);
      }
    },
    async loadMoreUserPrintLayouts(hideSpiner) {
      let userLayouts = [];
      if (this.tabType === LAYOUT_PAGE_TYPE.SINGLE_PAGE.id) {
        if (this.currentPageUserSingleLayouts < 0) {
          this.isLoadingPrintLayouts = false;
          return;
        }
        this.currentPageUserSingleLayouts++;
        userLayouts = await this.getCustom(
          this.tabType,
          this.currentPageUserSingleLayouts,
          hideSpiner
        );
        if (userLayouts.length < this.perPages) {
          this.isLoadingPrintLayouts = false;
          this.currentPageUserSingleLayouts = -1;
        }
      } else {
        if (this.currentPageUserSpreadLayouts < 0) {
          this.isLoadingPrintLayouts = false;
          return;
        }
        this.currentPageUserSpreadLayouts++;
        userLayouts = await this.getCustom(
          this.tabType,
          this.currentPageUserSpreadLayouts,
          hideSpiner
        );
        if (userLayouts.length < this.perPages) {
          this.isLoadingPrintLayouts = false;
          this.currentPageUserSpreadLayouts = -1;
        }
      }
      userLayouts.forEach(item => {
        const index = this.printLayouts.findIndex(
          layout => layout.id === item.id
        );
        if (index < 0) this.printLayouts.push(item);
      });
      this.printLayouts.sort((item1, item2) => {
        if (item1.isCustom && item2.isCustom) return item1.id - item2.id;
      });
    },
    async onLoadMorePrintTheme() {
      if (this.currentPagePrintTheme < 0) {
        this.isLoadingPrintTheme = false;
        return;
      }
      this.currentPagePrintTheme++;
      const themes = await getThemesApi(
        false,
        this.currentPagePrintTheme,
        true
      );
      this.themesOptions = [...this.themesOptions, ...themes];
      this.themesOptions.sort((item1, item2) => item1.id - item2.id);
      if (themes.length < this.perPages) {
        this.isLoadingPrintTheme = false;
        this.currentPagePrintTheme = -1;
      }
    }
  }
};
